"use client";

/**
 * WaterfallScrollPin — Niche v3 Scene 2 (Market) main wow-moment.
 *
 * Spec: wireframe-v3.md §3.2 + concept-v3.md DM-3 ("$4.2B becomes my $47K").
 *
 * Pin mechanism — both variants now CSS sticky:
 *   The mobile path was migrated from the JS-sentinel `useScrollPin` +
 *   `position: fixed; top: 0` pattern to a CSS sticky chart+narrative
 *   pinned together at `top-[104px]`. Reason: the niche detail page has
 *   two sticky bars stacked at the top (global NavbarShell at top-0, ~56px,
 *   z-50; NicheDetailV2 "← Back" sub-bar at top-14, ~48px, z-30). With the
 *   chart `fixed` at top-0 it rendered *under* both bars and the back-bar
 *   visibly disappeared while the chart "fell through" into the navbar
 *   layer. CSS sticky with an explicit offset matches the working pattern
 *   from `src/app/landing-v2/scenes/Scene2TheGap.tsx#MobileWaterfall`,
 *   which solves the same overlap. iOS Safari sticky-inside-long-container
 *   bug (the original reason for the JS path) is gone in iOS 16+, and the
 *   container is parented to <main className="overflow-x-clip"> (S72 fix)
 *   which keeps the viewport as the sticky scroll ancestor.
 *
 * Layout variants (Tailwind-gated, not separate codepaths):
 *   Mobile (< lg):
 *     Chart + active narrative pinned together at `sticky top-[104px]`
 *     (under NavbarShell + back-bar). Single AnimatePresence narrative
 *     crossfade driven by scrollYProgress band. Container 245vh.
 *   Desktop (≥ lg):
 *     Sticky 2-col grid at `top-36`. Narrative pinned in left 66%, chart
 *     sidecar in right 33%. Container 110vh.
 *
 * Reduced motion:
 *   Hook enabled=false path → no pin, no scroll-driven transforms. Bars and
 *   narrative render as a static vertical stack. `useReducedMotion()` gates
 *   the whole PinnedWaterfall tree.
 *
 * Why fork over Plan v2's WaterfallScrollPin:
 *   Plan's version consumes `MathSectionV2["waterfall"]` rows (label/value/type).
 *   Niche v3 consumes four human-readable display strings
 *   (tam / competitionShave / acquisitionShave / som) + parses USD via
 *   parseUSDAmount(). Different data contract, different narrative.
 *
 * Fallback:
 *   If any of tam / som is missing OR parseUSDAmount returns null for both,
 *   we render a minimal prose summary and skip the chart. Upstream
 *   `MarketScene` is the better place to decide between WaterfallScrollPin
 *   and a `marketSize` fallback, but this component is defensive in case
 *   it's mounted with partial data.
 */

import {
  AnimatePresence,
  motion,
  useMotionValueEvent,
  useReducedMotion,
  useScroll,
  useTransform,
  type MotionValue,
} from "framer-motion";
import { useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { parseUSDAmount } from "@/lib/niche-accent-tokens";
import { AnimatedCounter } from "@/components/shared-primitives/AnimatedCounter";

/**
 * Pick the active narrative index from scrollYProgress.
 *
 * Why state-driven active index (not per-block useTransform opacity):
 *   Mirrors the fix in `src/app/landing-v2/scenes/Scene2TheGap.tsx` —
 *   4 parallel useTransform subscribers to the same scrollYProgress MV
 *   hit framer-motion v12 subscription-staleness races, causing the
 *   narrative to freeze on one block while the bars continued animating.
 *   One useMotionValueEvent + one AnimatePresence child = single source
 *   of truth, clean crossfade, no overlapping text.
 *
 * Bands are equal quarters of the 0→1 scroll band. Half-open at the lower
 * bound, inclusive at the upper — progress=1.0 stays on SOM (isLast).
 */
function activeBarIndex(progress: number): number {
  if (progress >= 0.75) return 3;
  if (progress >= 0.5) return 2;
  if (progress >= 0.25) return 1;
  return 0;
}

/**
 * Optional per-step enrichment. All fields independently optional — MarketScene
 * composes what it can from the niche and omits the rest. Keeps WaterfallScrollPin
 * reusable (legacy callers can ignore the prop).
 */
export interface WaterfallEnrichment {
  /** Chips shown under THE POND hero number (competition/difficulty/time-to-$). */
  pondChips?: Array<{
    label: string;
    value: string;
    tone?: "neutral" | "amber" | "emerald" | "red" | "orange";
  }>;
  /** Competition level override ("low"/"medium"/"high") for THE GAP frame-inversion chip. */
  competitionLevel?: "low" | "medium" | "high";
  /** Top 3 distribution channels shown under YOUR EDGE body. */
  channels?: Array<{ name: string; members?: string; type?: string }>;
  /**
   * Revenue ladder under YEAR 1 counter — ordered Week 3 → Month 3 → Month 12 etc.
   * `highlight=true` lights emerald (peak/target), else muted label tone.
   */
  revenueLadder?: Array<{
    label: string;
    value: string;
    highlight?: boolean;
  }>;
  /** Optional micro-echo line under YEAR 1 counter (e.g. nineDayHook). */
  microEcho?: string;
  /** VisualGradient.descriptor — sensory phrase shown near POND ("Calm compounding energy"). */
  moodDescriptor?: string;
  /** Mood accent hex — drives ambient background per activeIdx. */
  accentHex?: string;
}

interface WaterfallScrollPinProps {
  /** TAM display string — e.g. "$4.2B". */
  tam: string;
  /** Competition shave display — e.g. "-65% held by 3 incumbents". */
  competitionShave: string;
  /** Acquisition shave display — e.g. "-20% hard to reach". */
  acquisitionShave: string;
  /** Serviceable obtainable market — e.g. "$47k/mo". */
  som: string;
  /** Optional per-step enrichment composed from the parent niche. */
  enrichment?: WaterfallEnrichment;
  /** Extra classes on outer container. */
  className?: string;
}

type Step = {
  kind: "tam" | "competition" | "acquisition" | "som";
  label: string;
  /** Human-readable display (passthrough from prop). */
  display: string;
  /** Parsed numeric value (best-effort, may be 0). */
  numeric: number;
  /** Bar width ratio vs TAM (0..1). */
  widthRatio: number;
  /** Visual treatment for the bar fill. */
  tone: "amber" | "red" | "orange" | "emerald";
};

/** Percentage-style string ("-65%") → ratio 0.65. Returns 0 if not parseable. */
function parsePercentShave(s: string): number {
  const m = s.match(/(\d+(?:\.\d+)?)\s*%/);
  if (!m) return 0;
  const v = parseFloat(m[1]);
  if (Number.isNaN(v)) return 0;
  return Math.max(0, Math.min(1, v / 100));
}

/**
 * Compute step ratios relative to TAM so bar widths tell the visual story.
 *
 * Logic:
 *   tam            → 1.0            (full width)
 *   competition    → 1 - pct1       (incumbents eat pct1)
 *   acquisition    → that × (1-pct2)
 *   som            → whatever remains (clamped to max 0.15 for visibility)
 *
 * If SOM numeric & TAM numeric are both > 0, we prefer numeric ratio over
 * chained percent math to stay honest to provided values.
 */
function computeSteps(
  tam: string,
  competitionShave: string,
  acquisitionShave: string,
  som: string,
): Step[] {
  const tamNum = parseUSDAmount(tam) ?? 0;
  const somNum = parseUSDAmount(som) ?? 0;

  const pctComp = parsePercentShave(competitionShave);
  const pctAcq = parsePercentShave(acquisitionShave);

  const afterComp = 1 - pctComp;
  const afterAcq = afterComp * (1 - pctAcq);
  const somRatio =
    tamNum > 0 && somNum > 0
      ? Math.max(0.02, Math.min(0.2, somNum / tamNum))
      : Math.max(0.02, Math.min(0.2, afterAcq));

  return [
    {
      kind: "tam",
      label: "The pond",
      display: tam,
      numeric: tamNum,
      widthRatio: 1,
      tone: "amber",
    },
    {
      kind: "competition",
      label: "The gap",
      display: competitionShave,
      numeric: tamNum * pctComp,
      widthRatio: afterComp,
      tone: "red",
    },
    {
      kind: "acquisition",
      label: "Your edge",
      display: acquisitionShave,
      numeric: tamNum * (1 - afterAcq) - tamNum * pctComp,
      widthRatio: afterAcq,
      tone: "orange",
    },
    {
      kind: "som",
      label: "Year 1",
      display: som,
      numeric: somNum,
      widthRatio: somRatio,
      tone: "emerald",
    },
  ];
}

/**
 * Detect SOM cadence from display string so we can compound monthly into an
 * annual crescendo ($12K/mo → $145K/yr). Only `/mo` values compound; `/yr`
 * and unknowns render as-is.
 */
function somAnnualFromMonthly(
  display: string,
  numeric: number,
): number | null {
  if (!numeric || numeric <= 0) return null;
  if (!/\/\s*mo\b/i.test(display)) return null;
  return numeric * 12;
}

/** Format bytes-style compact USD — "$4.2B", "$47k", etc. */
function formatCompactUSD(n: number): string {
  if (n <= 0) return "$0";
  if (n >= 1_000_000_000) {
    const v = n / 1_000_000_000;
    return `$${v >= 10 ? Math.round(v) : v.toFixed(1)}B`;
  }
  if (n >= 1_000_000) {
    const v = n / 1_000_000;
    return `$${v >= 10 ? Math.round(v) : v.toFixed(1)}M`;
  }
  if (n >= 1_000) {
    const v = n / 1_000;
    return `$${v >= 10 ? Math.round(v) : v.toFixed(1)}k`;
  }
  return `$${Math.round(n)}`;
}

const TONE_CLASSES: Record<
  Step["tone"],
  { bar: string; text: string; label: string }
> = {
  amber: {
    bar: "bg-gradient-to-r from-amber-400/70 to-orange-400/60",
    text: "text-amber-200",
    label: "text-amber-300/80",
  },
  red: {
    bar: "bg-gradient-to-r from-red-500/55 to-rose-500/45",
    text: "text-red-200",
    label: "text-red-300/80",
  },
  orange: {
    bar: "bg-gradient-to-r from-orange-500/55 to-amber-500/45",
    text: "text-orange-200",
    label: "text-orange-300/80",
  },
  emerald: {
    bar: "bg-gradient-to-r from-emerald-400/80 to-teal-400/70",
    text: "text-emerald-200",
    label: "text-emerald-300/80",
  },
};

/* ══════════════════ Component ══════════════════ */

export function WaterfallScrollPin({
  tam,
  competitionShave,
  acquisitionShave,
  som,
  enrichment,
  className,
}: WaterfallScrollPinProps) {
  const reduced = useReducedMotion();

  const steps = computeSteps(tam, competitionShave, acquisitionShave, som);

  // If neither TAM nor SOM parse, fall back to prose (defensive — MarketScene
  // normally won't mount us in that case).
  const fallback =
    (!parseUSDAmount(tam) || !parseUSDAmount(som)) &&
    (!tam.trim() || !som.trim());
  if (fallback) {
    return (
      <div
        className={cn(
          "rounded-2xl border border-subtle bg-surface-card p-6 text-white/75",
          className,
        )}
      >
        <p className="font-heading text-lg">
          Market is {tam || "sized in this range"}. Realistic slice{" "}
          {som || "to be validated"}.
        </p>
      </div>
    );
  }

  const ariaLabel = `Market waterfall — TAM ${tam}, ${competitionShave}, ${acquisitionShave}, SOM ${som}.`;

  // Reduced motion → static stack (no pin, no progress-driven transforms).
  if (reduced) {
    return (
      <div
        role="img"
        aria-label={ariaLabel}
        className={cn(
          "rounded-2xl border border-subtle bg-surface-card p-5 lg:p-8",
          className,
        )}
      >
        <p className="mb-5 text-[10px] font-semibold uppercase tracking-[0.2em] text-amber-400/70">
          Market waterfall
        </p>
        <div className="space-y-5">
          {steps.map((step, i) => (
            <StaticBarRow key={step.kind} step={step} index={i} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <PinnedWaterfall
      steps={steps}
      ariaLabel={ariaLabel}
      enrichment={enrichment}
      className={className}
    />
  );
}

/* ══════════════════ Pinned cinema variant — unified mobile + desktop ══════════════════ */

/**
 * Mobile: CSS sticky chart+narrative at `top-[104px]` (under NavbarShell +
 *           back-bar). Single AnimatePresence narrative crossfade driven by
 *           scrollYProgress band. Mirrors landing-v2 Scene2TheGap MobileWaterfall.
 * Desktop: CSS sticky pin + AnimatePresence crossfade of a single active
 *           narrative block. State-driven active index avoids framer-motion v12
 *           subscription staleness that was making the left-column text overlap
 *           on scroll.
 */
function PinnedWaterfall({
  steps,
  ariaLabel,
  enrichment,
  className,
}: {
  steps: Step[];
  ariaLabel: string;
  enrichment?: WaterfallEnrichment;
  className?: string;
}) {
  const desktopContainerRef = useRef<HTMLDivElement>(null);
  const mobileContainerRef = useRef<HTMLDivElement>(null);

  // Desktop: useScroll on container → progress band for bars + active narrative.
  // Offsets mirror Scene2TheGap: start when the container hits the pin line
  // (top 15%) and finish when it leaves the pin runway (end 80%).
  const { scrollYProgress: desktopProgress } = useScroll({
    target: desktopContainerRef,
    offset: ["start 15%", "end 80%"],
  });

  // Mobile: same pattern but tighter offsets (start 20%, end 88%) so the
  // 245vh runway maps onto the 4 narrative bands without dead scroll
  // before pin engage / after the last band finishes.
  const { scrollYProgress: mobileProgress } = useScroll({
    target: mobileContainerRef,
    offset: ["start 20%", "end 88%"],
  });

  const [desktopActiveIdx, setDesktopActiveIdx] = useState(0);
  useMotionValueEvent(desktopProgress, "change", (v) => {
    const next = activeBarIndex(v);
    setDesktopActiveIdx((prev) => (prev === next ? prev : next));
  });
  const desktopActiveStep = steps[desktopActiveIdx] ?? steps[0];

  const [mobileActiveIdx, setMobileActiveIdx] = useState(0);
  useMotionValueEvent(mobileProgress, "change", (v) => {
    const next = activeBarIndex(v);
    setMobileActiveIdx((prev) => (prev === next ? prev : next));
  });
  const mobileActiveStep = steps[mobileActiveIdx] ?? steps[0];

  // Ambient hue per activeIdx — mirrors step tone. When enrichment.accentHex is
  // provided, the emerald-accent pass on YEAR 1 is tinted by niche mood
  // (warm-amber niches → amber-weighted emerald, cool-emerald → pure emerald).
  const ambientByKind: Record<Step["kind"], string> = {
    tam: "251, 191, 36",       // amber-400
    competition: "244, 63, 94", // rose-500
    acquisition: "251, 146, 60", // orange-400
    som: "52, 211, 153",        // emerald-400
  };
  const activeAmbient = ambientByKind[desktopActiveStep.kind];

  return (
    <>
      {/* ─────────────── Desktop (≥lg) ─────────────── */}
      <div
        ref={desktopContainerRef}
        role="img"
        aria-label={ariaLabel}
        className={cn("relative mt-4 hidden lg:block", className)}
        // 110vh runway: enough for 4 narrative crossfades + light pin hold
        // before release into WHY NOW. Was 130vh when pin block was fixed
        // ~680px; now that the pin block sizes to content (~400-450px) the
        // runway shrinks correspondingly.
        style={{ height: "110vh" }}
      >
        {/* Ambient background — radial tint per activeIdx. -z-10 keeps it
            behind the sticky content but visible through the translucent bars card. */}
        <motion.div
          aria-hidden
          className="pointer-events-none absolute inset-0 -z-10"
          animate={{
            background: `radial-gradient(ellipse 80% 60% at 20% 40%, rgba(${activeAmbient}, 0.10) 0%, rgba(${activeAmbient}, 0.03) 40%, transparent 70%)`,
          }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        />

        {/* Sticky offset `top-36` (144px) = sticky back-bar (56 + ~40) +
            breathing room. `items-stretch` (grid default) equalises the two
            column heights so visual balance holds across narrative swaps.
            The sidecar's natural height (~400px with 4 bars) drives the row;
            left col stretches to match and centers its (often shorter)
            narrative vertically inside that row. */}
        <div className="sticky top-36 grid min-h-[420px] grid-cols-3 gap-8">
          {/* Left column (66%) — active narrative, absolutely-positioned and
              vertically centered. `popLayout` + flex-flow caused a visible
              height jump when swapping blocks of different heights (e.g.
              GAP=short → EDGE=medium → YEAR1=tall): exiting block dropped
              out of flow, next block rebuilt from y=24, `justify-center`
              recomputed center every frame → text visibly ducked down then
              snapped up. Absolute-positioning overlays blocks in the same
              center point; crossfade runs without touching layout. */}
          <div className="relative col-span-2">
            <AnimatePresence initial={false}>
              <DesktopNarrativeBlock
                key={desktopActiveStep.kind}
                step={desktopActiveStep}
                enrichment={enrichment}
              />
            </AnimatePresence>
          </div>

          {/* Right column (33%) — pinned chart sidecar, sizes to bars. */}
          <div className="col-span-1 rounded-2xl border border-subtle bg-surface-card/80 p-4 opacity-75">
            <p className="mb-4 text-[9px] font-semibold uppercase tracking-[0.2em] text-amber-400/60">
              Funnel
            </p>
            <div className="flex flex-col gap-3">
              {steps.map((step, i) => (
                <PinnedBarRow
                  key={step.kind}
                  step={step}
                  index={i}
                  total={steps.length}
                  scrollYProgress={desktopProgress}
                  isActive={i === desktopActiveIdx}
                  compact
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ─────────────── Mobile (<lg) — CSS sticky chart + narrative ─────────────── */}
      {/* Pin offset reads from `--waterfall-pin-top` (set on niche <main>);
          fallback 124px clears NavbarShell (sticky top-0 z-50, ~64px rendered)
          + NicheDetailV2 back-bar (sticky top-14 z-30, ~60px rendered) plus a
          small breathing margin. Earlier 104px undershot actual rendered
          heights — the chart's "MARKET WATERFALL" chip clipped under the
          navbar's backdrop-blur band. When the niche page tucks the back-bar
          away on scroll-down it retracts the var to 76px so the chart slides
          up to fill the freed band — the `transition-[top]` smooths that
          handover so the chart doesn't jump. Plan/standalone consumers don't
          set the var → fallback applies. z-10 keeps the chart card behind
          both bars during scroll so it doesn't overpaint their backdrop-blur.
          Pattern copied from src/app/landing-v2/scenes/Scene2TheGap.tsx
          #MobileWaterfall. */}
      <div
        ref={mobileContainerRef}
        role="img"
        aria-label={ariaLabel}
        className={cn("relative mt-4 lg:hidden", className)}
        style={{ height: "245svh" }}
      >
        <div className="sticky top-[var(--waterfall-pin-top,124px)] z-10 px-4 transition-[top] duration-200 ease-out max-[430px]:px-3">
          <div className="rounded-2xl border border-subtle bg-surface-card/95 p-4 ring-1 ring-inset ring-white/5 backdrop-blur-sm max-[430px]:p-3">
            <p
              aria-hidden
              className="mb-3 text-[9px] font-semibold uppercase tracking-[0.2em] text-amber-400/70 max-[430px]:mb-2"
            >
              Market waterfall
            </p>
            <div className="space-y-3 max-[430px]:space-y-2">
              {steps.map((step, i) => (
                <PinnedBarRow
                  key={step.kind}
                  step={step}
                  index={i}
                  total={steps.length}
                  scrollYProgress={mobileProgress}
                  isActive={i === mobileActiveIdx}
                  compact
                />
              ))}
            </div>
          </div>

          <div className="mt-4 px-1 max-[430px]:mt-3">
            <AnimatePresence mode="wait" initial={false}>
              <MobileActiveNarrative
                key={mobileActiveStep.kind}
                step={mobileActiveStep}
                enrichment={enrichment}
              />
            </AnimatePresence>
          </div>
        </div>
      </div>
    </>
  );
}

/* ══════════════════ Pinned bar row (shared mobile + desktop) ══════════════════ */

/**
 * Single bar-row primitive. `compact` prop switches to mobile sizing (9px
 * label, smaller track, tighter gaps). Both variants drive width from the
 * same scrollYProgress band, so motion matches across breakpoints.
 */
function PinnedBarRow({
  step,
  index,
  total,
  scrollYProgress,
  compact = false,
  isActive = false,
}: {
  step: Step;
  index: number;
  total: number;
  scrollYProgress: MotionValue<number>;
  compact?: boolean;
  isActive?: boolean;
}) {
  const tone = TONE_CLASSES[step.tone];
  const start = index / total;
  const end = (index + 1) / total;
  const maxPct = Math.max(4, step.widthRatio * 100);

  const widthMV = useTransform(scrollYProgress, [start, end], [0, maxPct], {
    clamp: true,
  });
  const widthCSS = useTransform(widthMV, (v) => `${v}%`);

  // Short numeric badge shown on the right of the bar (sidecar compact mode
  // only). GAP + EDGE LLM strings would wrap into unreadable 15-char columns —
  // parse just the -X% or $X hit, leave the prose for the left narrative.
  const numericHit = (() => {
    if (step.kind === "som" && step.numeric > 0) return null; // rendered via AnimatedCounter below
    if (step.kind === "tam") return step.display;
    const pctMatch = step.display.match(/-?\s*\d+(?:\.\d+)?\s*%/);
    if (pctMatch) return pctMatch[0].replace(/\s+/g, "");
    const usdMatch = step.display.match(/\$\s*\d+(?:[.,]\d+)?\s*[KMBkmb]?/);
    if (usdMatch) return usdMatch[0].replace(/\s+/g, "");
    return "";
  })();

  return (
    <div
      className={cn(
        "group flex flex-col gap-1.5 rounded-lg px-1 py-1 transition-colors",
        compact && "max-[430px]:gap-1 max-[430px]:py-0.5",
        isActive && "bg-white/[0.04]",
      )}
    >
      <div className="flex items-baseline justify-between gap-2">
        <span
          className={cn(
            "font-semibold uppercase",
            compact
              ? "text-[10px] tracking-[0.18em] max-[430px]:text-[9px] max-[430px]:tracking-[0.15em]"
              : "text-[11px] tracking-[0.2em]",
            tone.label,
            isActive && "opacity-100",
            !isActive && "opacity-70",
          )}
        >
          {step.label}
        </span>
        <span
          className={cn(
            "shrink-0 text-right font-heading font-semibold tabular-nums",
            compact ? "text-[11px] max-[430px]:text-[10px]" : "text-sm",
            tone.text,
            !isActive && "opacity-75",
          )}
        >
          {step.kind === "som" && step.numeric > 0 ? (
            <AnimatedCounter
              from={0}
              to={step.numeric}
              formatter={formatCompactUSD}
              suffix={step.display.includes("/mo") ? "/mo" : ""}
            />
          ) : (
            numericHit
          )}
        </span>
      </div>
      <div
        aria-hidden
        className={cn(
          "relative w-full overflow-hidden bg-white/[0.04]",
          compact ? "h-10 rounded-md max-[430px]:h-7" : "h-8 rounded-lg",
        )}
      >
        <motion.div
          style={{ width: widthCSS }}
          className={cn(
            "absolute inset-y-0 left-0 will-change-[width]",
            compact ? "rounded-md" : "rounded-lg",
            tone.bar,
          )}
        />
      </div>
    </div>
  );
}

/* ══════════════════ Desktop narrative (AnimatePresence crossfade) ══════════════════ */

/**
 * No scrollYProgress subscription — parent decides which step to mount.
 * AnimatePresence handles enter/exit crossfade in the same absolute slot.
 *
 * Per-kind composition: each step renders its own cluster of hero typography +
 * optional enrichment (chips for POND, channel stack for EDGE, revenue ladder
 * for YEAR 1). Generic "label + display" fallback if no enrichment matches.
 */
function DesktopNarrativeBlock({
  step,
  enrichment,
}: {
  step: Step;
  enrichment?: WaterfallEnrichment;
}) {
  const tone = TONE_CLASSES[step.tone];

  return (
    <motion.article
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
      // Absolute + `top-1/2 -translate-y-1/2` centers the active block in
      // the parent column without participating in flow. Swap-to-swap never
      // shifts siblings or self-position — the only delta is opacity/y,
      // so no perceptible ducking or snapping. `pointer-events-none`
      // keeps the exit-block from stealing hovers during the crossfade.
      className="pointer-events-none absolute inset-x-0 top-1/2 flex -translate-y-1/2 flex-col"
    >
      <p
        className={cn(
          "mb-3 text-[11px] font-semibold uppercase tracking-[0.22em]",
          tone.label,
        )}
      >
        {step.label}
      </p>

      {step.kind === "tam" && (
        <PondBlock step={step} tone={tone} enrichment={enrichment} />
      )}
      {step.kind === "competition" && (
        <GapBlock step={step} tone={tone} enrichment={enrichment} />
      )}
      {step.kind === "acquisition" && (
        <EdgeBlock step={step} tone={tone} enrichment={enrichment} />
      )}
      {step.kind === "som" && (
        <Year1Block step={step} tone={tone} enrichment={enrichment} />
      )}
    </motion.article>
  );
}

/* ══════════════════ Per-kind narrative blocks ══════════════════ */

type Tone = (typeof TONE_CLASSES)[Step["tone"]];

const CHIP_TONE: Record<
  NonNullable<NonNullable<WaterfallEnrichment["pondChips"]>[number]["tone"]>,
  string
> = {
  neutral: "border-white/15 bg-white/[0.03] text-white/80",
  amber: "border-amber-400/30 bg-amber-400/[0.06] text-amber-200",
  emerald: "border-emerald-400/30 bg-emerald-400/[0.06] text-emerald-200",
  red: "border-rose-500/30 bg-rose-500/[0.06] text-rose-200",
  orange: "border-orange-500/30 bg-orange-500/[0.06] text-orange-200",
};

function Chip({
  label,
  value,
  tone = "neutral",
}: {
  label: string;
  value: string;
  tone?: keyof typeof CHIP_TONE;
}) {
  return (
    <div
      className={cn(
        "inline-flex items-center gap-2 rounded-full border px-3 py-1.5",
        CHIP_TONE[tone],
      )}
    >
      <span className="text-[9px] font-semibold uppercase tracking-[0.18em] opacity-70">
        {label}
      </span>
      <span className="font-heading text-sm font-semibold">{value}</span>
    </div>
  );
}

function PondBlock({
  step,
  tone,
  enrichment,
}: {
  step: Step;
  tone: Tone;
  enrichment?: WaterfallEnrichment;
}) {
  return (
    <>
      <p
        className={cn(
          "font-heading font-black leading-[0.95] tracking-tight",
          "text-4xl sm:text-6xl lg:text-7xl",
          tone.text,
        )}
      >
        {step.display}
      </p>
      <p className="mt-3 max-w-md text-sm text-white/60 sm:text-base">
        in play every year · that’s the body of water
      </p>
      {enrichment?.pondChips && enrichment.pondChips.length > 0 && (
        <div className="pointer-events-auto mt-4 flex flex-wrap gap-2 sm:mt-6">
          {enrichment.pondChips.map((chip, i) => (
            <Chip
              key={`${chip.label}-${i}`}
              label={chip.label}
              value={chip.value}
              tone={chip.tone}
            />
          ))}
        </div>
      )}
      {enrichment?.moodDescriptor && (
        <p className="mt-4 max-w-md text-sm italic text-white/45 sm:mt-6">
          {enrichment.moodDescriptor}
        </p>
      )}
    </>
  );
}

function GapBlock({
  step,
  tone,
  enrichment,
}: {
  step: Step;
  tone: Tone;
  enrichment?: WaterfallEnrichment;
}) {
  return (
    <>
      <p
        className={cn(
          "font-heading font-bold leading-tight",
          "text-2xl sm:text-3xl lg:text-4xl",
          tone.text,
        )}
      >
        {step.display}
      </p>
      {enrichment?.competitionLevel && (
        <div className="pointer-events-auto mt-4 inline-flex items-center gap-2 rounded-xl border border-emerald-400/30 bg-emerald-400/[0.06] px-3 py-2.5 sm:mt-6 sm:gap-3 sm:px-4 sm:py-3">
          <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-emerald-300/80">
            Competition
          </span>
          <span className="font-heading text-lg font-bold text-emerald-200">
            {enrichment.competitionLevel.toUpperCase()}
          </span>
          <span className="text-xs text-white/50 max-[430px]:hidden">
            {enrichment.competitionLevel === "low"
              ? "— incumbents hold accounts, not attention"
              : enrichment.competitionLevel === "medium"
                ? "— room still open if you're specific"
                : "— pick a sharper wedge"}
          </span>
        </div>
      )}
    </>
  );
}

function EdgeBlock({
  step,
  tone,
  enrichment,
}: {
  step: Step;
  tone: Tone;
  enrichment?: WaterfallEnrichment;
}) {
  const topChannels = (enrichment?.channels ?? []).slice(0, 3);
  return (
    <>
      <p
        className={cn(
          "font-heading font-bold leading-snug",
          "text-xl sm:text-2xl lg:text-3xl",
          tone.text,
        )}
      >
        {step.display}
      </p>
      {topChannels.length > 0 && (
        <div className="pointer-events-auto mt-4 sm:mt-6">
          <p className="mb-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-white/50">
            Where they already gather
          </p>
          <div className="flex flex-col gap-2">
            {topChannels.map((ch) => (
              <div
                key={ch.name}
                className="flex items-center justify-between gap-3 rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 sm:gap-4 sm:px-4 sm:py-2.5"
              >
                <span className="font-heading text-sm font-semibold text-white/90 sm:text-base">
                  {ch.name}
                </span>
                {ch.members && (
                  <span className="shrink-0 rounded-full bg-orange-500/10 px-2.5 py-0.5 text-xs font-semibold text-orange-200">
                    {ch.members}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

function Year1Block({
  step,
  tone,
  enrichment,
}: {
  step: Step;
  tone: Tone;
  enrichment?: WaterfallEnrichment;
}) {
  const ladder = enrichment?.revenueLadder ?? [];
  const annual = somAnnualFromMonthly(step.display, step.numeric);
  return (
    <>
      {/* Counter explosion — the dopamine moment. */}
      {step.numeric > 0 ? (
        <>
          <p
            className={cn(
              "font-heading font-black leading-[0.95] tracking-tight tabular-nums",
              "text-4xl sm:text-6xl lg:text-7xl",
              tone.text,
            )}
          >
            <AnimatedCounter
              from={0}
              to={step.numeric}
              formatter={formatCompactUSD}
              suffix="/mo"
            />
          </p>
          {annual && (
            <p className="mt-2 text-sm uppercase tracking-[0.16em] text-emerald-300/70 sm:text-base sm:tracking-[0.18em]">
              ≈{" "}
              <span className="font-heading text-base font-semibold text-emerald-200 sm:text-lg">
                <AnimatedCounter
                  from={0}
                  to={annual}
                  formatter={formatCompactUSD}
                  suffix="/yr"
                />
              </span>{" "}
              year one
            </p>
          )}
        </>
      ) : (
        <p
          className={cn(
            "font-heading font-bold leading-tight",
            "text-2xl sm:text-3xl lg:text-4xl",
            tone.text,
          )}
        >
          {step.display}
        </p>
      )}

      {/* Staircase: Week 3 → Month 3 → Month 12. Each rung stagger-in. */}
      {ladder.length > 0 && (
        <div className="pointer-events-auto mt-4 sm:mt-6">
          <p className="mb-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-white/50">
            The climb
          </p>
          <div className="grid grid-cols-3 gap-2">
            {ladder.slice(0, 3).map((rung, i) => (
              <motion.div
                key={rung.label}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.15 + i * 0.12, ease: "easeOut" }}
                className={cn(
                  "rounded-lg border px-3 py-2.5",
                  rung.highlight
                    ? "border-emerald-400/40 bg-emerald-400/[0.08]"
                    : "border-white/10 bg-white/[0.03]",
                )}
              >
                <p
                  className={cn(
                    "text-[9px] font-semibold uppercase tracking-[0.18em]",
                    rung.highlight ? "text-emerald-300/80" : "text-white/50",
                  )}
                >
                  {rung.label}
                </p>
                <p
                  className={cn(
                    "mt-0.5 font-heading text-sm font-bold",
                    rung.highlight ? "text-emerald-100" : "text-white/85",
                  )}
                >
                  {rung.value}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {enrichment?.microEcho && (
        <p className="mt-4 max-w-lg text-sm italic text-white/55 sm:mt-5">
          {enrichment.microEcho}
        </p>
      )}
    </>
  );
}

/* ══════════════════ Mobile narrative (AnimatePresence crossfade — single active) ══════════════════ */

/**
 * Mobile variant — one active narrative block at a time, crossfaded by parent
 * <AnimatePresence> when the active step changes. Mirrors the desktop pattern
 * and matches landing-v2 Scene2TheGap MobileWaterfall: chart + narrative
 * pinned together so scrolling drives only the active-index swap, not a
 * physical translation through 4 stacked slots.
 *
 * `min-h-[200px]` reserves vertical room for the tallest narrative composition
 * (YEAR 1 with revenue ladder) so neighbouring scene content doesn't visibly
 * jump as the active block swaps. Composition is shared with the desktop
 * blocks (Pond / Gap / Edge / Year1) but rendered at mobile typography.
 */
function MobileActiveNarrative({
  step,
  enrichment,
}: {
  step: Step;
  enrichment?: WaterfallEnrichment;
}) {
  const tone = TONE_CLASSES[step.tone];

  return (
    <motion.article
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.2, ease: "easeOut" }}
      className="min-h-[200px] max-[430px]:min-h-[165px]"
    >
      <p
        className={cn(
          "mb-2 text-[10px] font-semibold uppercase tracking-[0.2em]",
          tone.label,
        )}
      >
        {step.label}
      </p>

      {step.kind === "tam" && (
        <PondBlock step={step} tone={tone} enrichment={enrichment} />
      )}
      {step.kind === "competition" && (
        <GapBlock step={step} tone={tone} enrichment={enrichment} />
      )}
      {step.kind === "acquisition" && (
        <EdgeBlock step={step} tone={tone} enrichment={enrichment} />
      )}
      {step.kind === "som" && (
        <Year1Block step={step} tone={tone} enrichment={enrichment} />
      )}
    </motion.article>
  );
}

/* ══════════════════ Static bar row (reduced-motion only) ══════════════════ */

function StaticBarRow({ step, index }: { step: Step; index: number }) {
  const tone = TONE_CLASSES[step.tone];
  const widthPct = Math.max(4, Math.round(step.widthRatio * 100));

  return (
    <div className="space-y-2" style={{ order: index }}>
      <div className="flex items-baseline justify-between gap-3">
        <span
          className={cn(
            "text-[10px] font-semibold uppercase tracking-[0.18em]",
            tone.label,
          )}
        >
          {step.label}
        </span>
        <span className={cn("font-heading text-sm font-semibold", tone.text)}>
          {step.kind === "som" && step.numeric > 0 ? (
            <AnimatedCounter
              from={0}
              to={step.numeric}
              formatter={formatCompactUSD}
              suffix={step.display.includes("/mo") ? "/mo" : ""}
            />
          ) : (
            step.display
          )}
        </span>
      </div>
      <div
        aria-hidden
        className="relative h-6 overflow-hidden rounded-lg bg-white/[0.04]"
      >
        <div
          className={cn("absolute inset-y-0 left-0 rounded-lg", tone.bar)}
          style={{ width: `${widthPct}%` }}
        />
      </div>
    </div>
  );
}
